import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Copy of {@link ApiKeyRevealDialogView}; each omitted key falls back to English. */
export interface ApiKeyRevealDialogLabels {
	title: string
	warning: string
	keyLabel: string
	copy: string
	copied: string
	done: string
	close: string
}

const DEFAULT_LABELS: ApiKeyRevealDialogLabels = {
	title: 'Your new API key',
	warning: 'Copy this key now and store it safely. It will not be shown again.',
	keyLabel: 'API key',
	copy: 'Copy',
	copied: 'Copied',
	done: 'Done',
	close: 'Close',
}

/**
 * Props for {@link ApiKeyRevealDialogView}.
 *
 * @property plaintextKey - The freshly created key; the dialog is open exactly while it is non-null.
 * @property copied - True once the copy action succeeded.
 * @property onClose - The app discards the plaintext on close.
 */
export interface ApiKeyRevealDialogViewProps {
	plaintextKey: string | null
	copied: boolean
	onCopy: () => void
	onClose: () => void
	labels?: Partial<ApiKeyRevealDialogLabels>
}

/** Shows a created key's plaintext once, with a copy action (organism, TO-516). */
export function ApiKeyRevealDialogView(props: ApiKeyRevealDialogViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<Dialog
			isOpen={props.plaintextKey !== null}
			onOpenChange={(open) => (open ? undefined : props.onClose())}
			title={text.title}
			labels={{ close: text.close }}
		>
			<div className="flex flex-col gap-3">
				<p role="note" className="text-ds-sm font-medium text-ds-danger">
					{text.warning}
				</p>
				<figure className="flex flex-col gap-1">
					<figcaption className="text-ds-sm text-ds-text-muted">{text.keyLabel}</figcaption>
					<code className="break-all rounded-ds-md border border-ds-border bg-ds-surface-muted p-3 font-mono text-ds-sm text-ds-text select-all">
						{props.plaintextKey}
					</code>
				</figure>
				<div className="flex items-center gap-2">
					<Button variant="secondary" onPress={props.onCopy}>
						{text.copy}
					</Button>
					<span role="status" className="text-ds-sm text-ds-text-muted">
						{props.copied ? text.copied : ''}
					</span>
				</div>
				<div className="mt-1 flex gap-2">
					<Button onPress={props.onClose}>{text.done}</Button>
				</div>
			</div>
		</Dialog>
	)
}
